<script lang="ts">
    import { onDestroy, tick, untrack } from 'svelte'
    import { SvelteSet } from 'svelte/reactivity'
    import type { Readable, Writable } from 'svelte/store'
    import { derived as sderived } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import { cloneDeep, isEqual, orderBy } from 'lodash-es'

    import { context as siteContext } from 'hydria/context'
    import { gettext as _, resolve, tgettext } from 'hydria/services/gettext'
    import { lg as sessionLg } from 'hydria/services/language'
    import { session } from 'hydria/services/session'
    import { formatPhoneNumber, formatPostalCode } from 'hydria/utils/strings'

    import { contactTypeOrder, cprCategories, nonIndividualCprCategoryCodes, sortContacts, tldRules } from 'wdom/services/contacts'
    import type { IAddressGroup, IContact, IContactCira, ICprCategory, TRestrictedContactType } from 'wdom/services/contacts'

    import type { IContactEdit, IContactGroup } from './interfaces'

    import ContactEditor from './ContactEditor.svelte'

    interface Props {
        // context == domain → only one;  cart (multiple) → at least one.
        context: 'domain' | 'multiple'
        // This allows to load up the proper rules, and show a domain list for the validity
        domains: string[]
        // The actual "initial, saved" contacts, on initialization, else these are "edits" from the parent component.
        // e.g. "undo" or "load revision from other domain" or such
        contacts: IContact[]
        // If true, the parent is doing something, like saving, and we should disable everything
        updating: boolean
        // On changes, we update this writable with the "edited data"
        contactsEdited: Writable<IContact[]>
        // When not null, we will overwrite contactsEdited with this source, then reset it to null.
        // It's a bit like a simple signal.
        setContacts?: undefined | null | IContact[]
        canEditRegistrant?: boolean
        tldValidity?: null | Writable<{ [key: string]: boolean }>
        inlineMode?: boolean
        addresses?: IAddressGroup[] | null
        resetContacts?: boolean
        lg?: string
    }

    let { context, domains, contacts, updating, contactsEdited, setContacts = $bindable(undefined), canEditRegistrant = false, tldValidity = null, inlineMode = false, addresses = null, resetContacts = $bindable(false), lg: inLg = undefined }: Props = $props()

    let lg = $derived(inLg != null ? inLg : session.miscData.force_prefered_lg ? session.miscData.prefered_lg : $sessionLg) as 'fr' | 'en'

    // "Signal" to load the contacts from the parent
    setContacts = null

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    let ready = $state(false)

    // The contact group assigned to this is edited in the modal
    // let contactGroupInEdition: IContactGroup | null = $state(null)
    let contactGroupInEdition: number | null = $state(null)

    // The version sent to the modal, which is essentially a copy of the actual
    // group... until we save
    // const contactGroupEdit: Writable<IContactGroup | null> = writable(null)
    const contactGroupEdit: IContactGroup | null = $derived.by(() => {
        if (contactGroupInEdition != null) {
            const cge = cloneDeep($state.snapshot(contactGroups[contactGroupInEdition]))
            cge.source = { ...cge.edit }
            return cge
        } else {
            return null
        }
    })

    let validityContexts: { [key: string]: boolean } = $derived.by(() => {
        const loc: { [key: string]: boolean } = {}
        if ($tldRules) {
            for (const tld of tldList) {
                if (tld == 'ca') loc.cira = true
                else if ($tldRules[tld] != null) {
                    loc[tld] = true
                    for (const ctype of ['tech', 'billing']) {
                        // if ($tldRules[tld][`no_type_${ctype}`] == null) requiredContactTypes.add(ctype)
                    }
                } else {
                    loc.general = true
                    // requiredContactTypes.add('tech')
                    // requiredContactTypes.add('billing')
                }
            }
        }
        return loc
    })
    let requiredContactTypes: SvelteSet<string> = $derived.by(() => {
        let loc = new Set(['registrant'])
        if ($tldRules) {
            for (const tld of tldList) {
                if (tld == 'ca') {
                    loc.add('admin')
                } else if ($tldRules[tld] != null) {
                    for (const ctype of ['admin', 'tech', 'billing']) {
                        if (!$tldRules[tld].includes(`no_type_${ctype}`)) loc.add(ctype)
                    }
                } else {
                    loc.add('admin')
                    loc.add('tech')
                    loc.add('billing')
                }
            }
        }
        return new SvelteSet(loc)
    })
    const tldList = [...new Set(domains.map((domain) => domain.split('.').slice(-1)[0]))]

    // ##############################################################  Load CPR lists
    // const sortedCprCategories = sderived(cprCategories, ($cprCategories) => {
    //     if ($cprCategories == null) return null
    //     return orderBy($cprCategories, [
    //         (cc) => {
    //             if (cc.code == 'CCT') return 0
    //             if (cc.code == 'CCO') return 1
    //             return 2
    //         },
    //     ])
    // })
    let cprIndex: Readable<{ [key: string]: ICprCategory }> = sderived(cprCategories, ($cprCategories) => {
        let indx = {}
        if ($cprCategories != null) {
            for (const cc of $cprCategories) {
                indx[cc.code] = cc
            }
        }
        return indx
    })

    // ##############################################################  Load Contact with log
    let contactGroups: IContactGroup[] = $state([])
    // const writableContactGroups: Array<Writable<IContactGroup>> = []

    function makeEmptyContactData(): IContactEdit {
        return {
            cira_prefered_lang: null,
            cira_cpr_category: null,
            voice: null,
            fax: null,
            name: null,
            email: null,
            city: null,
            postal_code: null,
            street: [],
            sp: null,
            country: null,
        } as unknown as IContactEdit
    }

    function loadContacts(contacts: IContact[]) {
        const cs: IContact[] = sortContacts(contacts)
        let lcontactGroups: IContactGroup[] = []

        let contactTypes = new Set()
        let billingCount = 0
        let techCount = 0

        for (const c of cs) {
            const ce = contactToContactEdit(c)
            const ctype = (c.type as any) == 'owner' ? 'registrant' : c.type

            const cg: IContactGroup = {
                type: ctype as TRestrictedContactType,
                id: ctype,
                source: ce,
                edit: { ...ce },
                dirty: false,
                invalid: false,
                // @ts-expect-error
                status: null,
                // fieldStatus: {},
                original: c,
            }
            lcontactGroups.push(cg)
            contactTypes.add(cg.type)
            if (cg.type == 'billing') billingCount++
            if (cg.type == 'tech') techCount++
        }

        function makeEmptyContactGroup(ctype: string) {
            const ce: IContactEdit = makeEmptyContactData()
            const cg: IContactGroup = {
                type: ctype as TRestrictedContactType,
                id: ctype,
                source: ce,
                edit: { ...ce },
                dirty: false,
                invalid: false,
                // @ts-expect-error
                status: null,
                fields: new Set(),
                // @ts-expect-error
                name: null,
            }
            return cg
        }

        // Add missing types
        const missingTypes = new Set([...requiredContactTypes].filter((x) => !contactTypes.has(x)))
        for (const ctype of missingTypes) {
            lcontactGroups.push(makeEmptyContactGroup(ctype))
            // contactTypes.add(ctype)
            if (ctype == 'billing') billingCount++
            if (ctype == 'tech') techCount++
        }

        // Add extra 'billing' for CIRA
        if (validityContexts.cira) {
            const maxBilling = 1
            while (billingCount < maxBilling) {
                lcontactGroups.push(makeEmptyContactGroup('billing'))
                billingCount++
            }
        }

        // Add extra 'tech' for CIRA
        if (validityContexts.cira) {
            const maxTech = context == 'domain' ? 3 : 1
            while (techCount < maxTech) {
                lcontactGroups.push(makeEmptyContactGroup('tech'))
                techCount++
            }
        }

        // Set the group names
        let techPos = 0
        for (const cg of lcontactGroups) {
            cg.id = cg.type
            switch (cg.type) {
                case 'registrant':
                    cg.name = tgettext('Registrant')
                    // FIXME: handle 'owner' as well
                    break
                case 'admin':
                    cg.name = tgettext('Admin')
                    break
                case 'billing':
                    cg.name = tgettext('Billing')
                    break
                case 'tech':
                    techPos++
                    cg.name = techPos > 1 ? tgettext('Technical {n}', { n: techPos }) : tgettext('Technical')
                    cg.id = `${cg.id}-${techPos}`
                    break
            }
        }

        // Sort the groups
        lcontactGroups = orderBy(lcontactGroups, [(c) => $contactTypeOrder[c.type]])

        // And finally, generate all the validation stuff by validating/comparing at least once.
        for (const cg of lcontactGroups) {
            validateContactGroup(cg)
        }

        contactGroups = lcontactGroups
        // Useless, but not really, it sets $editedContacts
        markUpdated()
    }

    // "Convert" a contact to the editable version
    function contactToContactEdit(contact: IContact): IContactEdit {
        const ce: IContactEdit = Object.fromEntries(Object.entries(contact).filter(([k, v]) => k != 'cira' && k != 'type')) as IContactEdit

        ce.cira_prefered_lang = contact.cira != null && (contact.cira.prefered_lang == 'fr' || contact.cira.prefered_lang == 'en') ? contact.cira.prefered_lang : lg
        // @ts-expect-error
        ce.cira_cpr_category = contact.cira != null ? contact.cira.cpr_category : null

        if (ce.street == null) ce.street = []
        let nv: string[] = []
        for (let line of ce.street) {
            if (line != null) line = line.trim()
            if (line != null && line != '') nv.push(line)
        }
        ce.street = nv

        return ce
    }

    function contactGroupToContact(cg: IContactGroup): IContact {
        const c: IContact = Object.fromEntries(Object.entries(cg.edit).filter(([k, v]) => !k.startsWith('cira_'))) as IContact

        c.type = cg.type
        c.cira = {
            id: cg.original && cg.original.cira && cg.original.cira.id ? cg.original.cira.id : null,
            agreement_version: cg.original && cg.original.cira && cg.original.cira.agreement_version ? cg.original.cira.agreement_version : null,
            prefered_lang: cg.edit.cira_prefered_lang,
            cpr_category: cg.edit.cira_cpr_category,
        } as unknown as IContactCira

        return c
    }

    function setupValidationDataOnGroup(cg: IContactGroup) {
        cg.keepFields = new Set()
        cg.requiredFields = new Set()

        // let reqFields = ['name', 'street', 'city', 'country', 'voice', 'sp', 'postal_code', 'email']

        if (validityContexts.cira) {
            let locFields = new Set(['cira_cpr_category', 'cira_prefered_lang', 'name', 'org', 'street', 'city', 'country', 'voice', 'fax', 'email', 'sp', 'postal_code'])
            cg.keepFields = new Set([...cg.keepFields, ...locFields])

            let reqFields = ['cira_cpr_category', 'cira_prefered_lang', 'name', 'street', 'city', 'country', 'voice', 'email']
            if (['CA', 'US'].includes(cg.edit.country)) reqFields = reqFields.concat(['sp', 'postal_code'])
            if (cg.type == 'registrant') {
                reqFields.push('cira_cpr_category')
                if (cg.edit.cira_cpr_category != null && $cprIndex[cg.edit.cira_cpr_category].classification == 'NI') {
                    reqFields.push('org')
                }
            }

            cg.requiredFields = new Set([...locFields].filter((x) => reqFields.includes(x)))
        }

        if (validityContexts.general) {
            cg.keepFields = new Set([...cg.keepFields, ...['name', 'org', 'street', 'city', 'country', 'voice', 'fax', 'emil', 'sp', 'postal_code']])
            cg.requiredFields = new Set([...cg.requiredFields, ...['name', 'street', 'city', 'country', 'voice', 'email', 'sp', 'postal_code']])
        }

        for (const tld of Object.keys(validityContexts)) {
            if ($tldRules[tld] == null) continue
            let locFields: string[] = []
            for (const field of ['name', 'org', 'street', 'city', 'country', 'voice', 'fax', 'sp', 'postal_code', 'email']) {
                if (!$tldRules[tld].includes(`no_field_${field}`)) locFields.push(field)
            }
            cg.keepFields = new Set([...cg.keepFields, ...locFields])
            // cg.requiredFields = new Set([...locFields].filter((x) => reqFields.includes(x)))
            // @ts-expect-error
            if (cg.requiredFields != null) cg.requiredFields = new Set([...locFields].filter((x) => cg.requiredFields.has(x)))
            else cg.requiredFields = new Set()
        }

        return cg
    }

    function compareContactGroup(cg: IContactGroup) {
        let dataSource = cg.source

        cg.fields = new Set()
        let baseFields = ['name', 'org', 'street', 'city', 'sp', 'postal_code', 'country', 'voice', 'fax', 'email']
        let allowPhoneExt = false

        for (const tld of Object.keys(validityContexts)) {
            if (tld == 'cira') {
                cg.fields = new Set([...cg.fields, ...baseFields])
                cg.fields.add('cira_cpr_category')
                cg.fields.add('cira_prefered_lang')
                allowPhoneExt = true
            } else if (tld == 'general') {
                cg.fields = new Set([...cg.fields, ...baseFields])
                allowPhoneExt = true
            } else {
                for (const field of baseFields) {
                    if (!$tldRules[tld] || !$tldRules[tld].includes(`no_field_${field}`)) {
                        cg.fields.add(field)
                    }
                }
                if (!$tldRules[tld] || !$tldRules[tld].includes(`no_field_phone_ext`)) {
                    allowPhoneExt = true
                }
            }
        }

        cg.hiddenFields = new Set()
        if (!allowPhoneExt) {
            for (const field of ['voice', 'fax']) {
                if (cg.fields.has(field)) cg.hiddenFields.add(`${field}_ext`)
            }
        }

        cg.sourceEmpty = true
        cg.editEmpty = true
        cg.dirty = false
        cg.canEdit = context != 'domain' || cg.type != 'registrant' || !tldList.includes('ca') || canEditRegistrant

        cg.fieldStatus = {}
        for (const field of cg.fields) {
            const fieldType = field == 'street' ? 'array' : 'string'
            if (fieldType == 'array' && (dataSource[field] == null || dataSource[field].length == 0)) dataSource[field] = []
            else if (fieldType == 'string' && (dataSource[field] == null || dataSource[field] == '')) dataSource[field] = null

            // Cleanup: remove empty array lines, convert empty strings to nulls
            if (!cg.edit[field]) {
                if (fieldType == 'array') {
                    let nv: string[] = []
                    for (let line of cg.edit[field]) {
                        if (line != null) line = line.trim()
                        if (line != null && line != '') nv.push(line)
                    }
                    cg.edit[field] = nv
                } else {
                    if (cg.edit[field] != null) {
                        cg.edit[field] = cg.edit[field].trim()
                    }
                    if (!cg.edit[field]) {
                        cg.edit[field] = null
                    }
                }
            }

            // Compare
            let isEmpty: boolean, sourceEmpty: boolean
            if (fieldType == 'array') {
                isEmpty = !cg.edit[field] || !cg.edit[field].length
                sourceEmpty = !cg.source[field] || !cg.source[field].length
            } else {
                isEmpty = !cg.edit[field]
                sourceEmpty = !cg.source[field]
            }

            if (!isEqual(cg.edit[field], cg.source[field])) {
                cg.fieldStatus[field] = !isEmpty ? 'dirty' : 'dirty-empty'
                cg.dirty = true
            } else if (isEmpty) {
                cg.fieldStatus[field] = 'empty'
            } else {
                cg.fieldStatus[field] = 'clean'
            }

            if (!sourceEmpty) cg.sourceEmpty = false
            if (!isEmpty) cg.editEmpty = false
        }

        return cg
    }

    function validateContactGroup(cg: IContactGroup) {
        setupValidationDataOnGroup(cg)
        compareContactGroup(cg)
        return updateGroupValidity(cg)
    }

    function updateGroupValidity(cg: IContactGroup) {
        cg.invalidFields = Object.fromEntries(Object.keys(validityContexts).map((k) => [k, new Set()]))

        cg.invalid = false
        let invCandidates: string[] = []
        for (const key of cg.requiredFields || []) {
            if (cg.edit[key] == null || cg.edit[key] == '') invCandidates.push(key)
        }
        if (cg.edit.street == null || !cg.edit.street.length) invCandidates.push('street')

        for (const tld of Object.keys(validityContexts)) {
            if ((tld == 'cira' && cg.type == 'billing') || ($tldRules[tld] && $tldRules[tld].includes(`no_type_${cg.type}`))) {
                cg.invalidFields[tld] = new Set()
                continue
            }
            // let genFields = ['name', 'street', 'city', 'sp', 'postal_code', 'country', 'voice', 'email']
            if (tld == 'general') {
                cg.invalidFields[tld] = new Set([...(cg.requiredFields || [])].filter((x) => invCandidates.includes(x)))
            } else if (tld == 'cira') {
                cg.invalidFields[tld] = new Set([...(cg.requiredFields || [])].filter((x) => invCandidates.includes(x)))

                if (['admin', 'billing', 'tech'].includes(cg.type) && cg.edit.cira_cpr_category && (!$cprIndex[cg.edit.cira_cpr_category] || $cprIndex[cg.edit.cira_cpr_category].classification == 'NI')) {
                    cg.invalidFields[tld].add('cira_cpr_category')
                }
            } else {
                cg.invalidFields[tld] = new Set([...(cg.requiredFields || [])].filter((x) => invCandidates.includes(x)))
            }
        }

        let globalInvalidFields = new Set()
        let atLeastOneValid = false
        cg.validity = { contextual: null } as any
        for (const [tld, fields] of Object.entries(cg.invalidFields)) {
            if (tld == 'cira' && cg.type == 'registrant' && !canEditRegistrant) {
                // @ts-expect-error
                cg.validity[tld] = true
            } else if (tld == 'cira' && (cg.type == 'tech' || cg.type == 'billing')) {
                // @ts-expect-error
                cg.validity[tld] = cg.editEmpty || !fields.size
            } else {
                // @ts-expect-error
                cg.validity[tld] = !fields.size
            }
        }

        // Compute "contextual"
        switch (context) {
            case 'multiple':
                cg.isRequired = requiredContactTypes.has(cg.type)
                cg.invalidFields.contextual = new Set()
                for (const [tld, isActive] of Object.entries(validityContexts)) {
                    if (!isActive) continue
                    if (cg.invalidFields[tld] && cg.invalidFields[tld].size) {
                        cg.invalidFields.contextual = new Set([...cg.invalidFields.contextual, ...cg.invalidFields[tld]])
                    }
                }
                break
            case 'domain':
                if (tldList[0] == 'ca') {
                    cg.invalidFields.contextual = new Set([...cg.invalidFields.cira])
                    // cg.isRequired = cg.type != 'tech' && cg.type != 'billing'
                    cg.isRequired = cg.type == 'registrant' || cg.type == 'admin'
                } else if (
                    // @ts-expect-error
                    cg.validity[tldList[0]] != null
                ) {
                    cg.invalidFields.contextual = new Set([...cg.invalidFields[tldList[0]]])
                    cg.isRequired = !$tldRules[tldList[0]].includes(`no_type_${cg.type}`)
                } else {
                    cg.invalidFields.contextual = new Set([...cg.invalidFields.general])
                    cg.isRequired = true
                }
                break
            // case 'template':
            //     cg.validity.contextual = atLeastOneValid
            //     cg.invalidFields.contextual = atLeastOneValid ? [] : globalInvalidFields
            //     break
        }
        // @ts-expect-error
        if (cg.validity.contextual == null) {
            // @ts-expect-error
            cg.validity.contextual = !cg.invalidFields.contextual.size
        }

        let dirty = false
        if (cg.fieldStatus)
            for (const fstatus of Object.values(cg.fieldStatus)) {
                if (fstatus == 'dirty' || fstatus == 'dirty-empty') {
                    dirty = true
                    break
                }
            }

        if (!cg.canEdit && context == 'domain' && cg.type == 'registrant' && tldList[0] == 'ca' && !canEditRegistrant) {
            // This is a CIRA registrant. Valid or not, it won't be saved on the backend.
            // So we should not prohibit saving if we can’t edit it.
            cg.status = 'ok'
            cg.invalid = false
        } else if (
            // @ts-expect-error
            cg.validity.contextual
        ) {
            cg.status = dirty ? 'dirty' : 'ok'
            cg.invalid = false
        } else if (cg.editEmpty && !cg.isRequired && cg.sourceEmpty) {
            cg.status = dirty ? 'dirty' : 'unset'
            cg.invalid = false
        } else if (cg.editEmpty && !cg.isRequired) {
            cg.status = 'clean'
            cg.invalid = false
        } else {
            cg.status = 'invalid'
            cg.invalid = true
        }

        return cg
    }

    function darkButtons(status: string) {
        return status == 'dirty'
    }

    let loadFromOtherOpen: { [key: string]: boolean } = $state({})

    function cprCategoryName(cat: string) {
        if ($cprIndex[cat] == null) return `? (${cat})`
        return $cprIndex[cat][`description_${$sessionLg}`] + ` (${cat})`
    }

    // ##############################################################  Utility
    function markUpdated() {
        contactsEdited.set(contactGroups.filter((cg) => !cg.editEmpty).map((cg) => contactGroupToContact(cg)))
        // Recompute global validity for each contexts

        if (tldValidity != null) {
            let contexts = Object.fromEntries(
                Object.entries(validityContexts).map(([k, v]) => {
                    return [k, true]
                }),
            )
            for (const cg of contactGroups) {
                if (cg.validity)
                    for (const [k, v] of Object.entries(cg.validity)) {
                        if (!v) contexts[k] = false
                    }
            }
            tldValidity.set(contexts)
        }
        contactGroups = contactGroups
    }

    // Load the stuff from $contactsEdited as the edited value
    function loadEditedContacts(contacts: IContact[]) {
        if (!ready) return

        const cgTypeIndex: { [key: string]: Array<IContactGroup> } = {}
        for (const cg of contactGroups) {
            if (!cgTypeIndex[cg.type]) cgTypeIndex[cg.type] = []
            cgTypeIndex[cg.type].push(cg)
        }

        for (const c of contacts) {
            if (!cgTypeIndex[c.type] || cgTypeIndex[c.type].length < 1) continue
            const cg = cgTypeIndex[c.type].shift() as IContactGroup
            cg.edit = { ...contactToContactEdit(c) }
            validateContactGroup(cg)
        }

        for (const cgList of Object.values(cgTypeIndex)) {
            for (const cg of cgList) {
                cg.edit = makeEmptyContactData()
                validateContactGroup(cg)
            }
        }
        markUpdated()
    }

    // ##############################################################  Copy / paste
    let cgClipboard: IContactGroup | undefined = $state(undefined)

    function copyContactGroup(cg: IContactGroup) {
        cgClipboard = cg
    }

    function pasteContactGroup(cg: IContactGroup) {
        if (cgClipboard == undefined) return

        const data = { ...cgClipboard.edit }
        if ($nonIndividualCprCategoryCodes.has(data.cira_cpr_category) && cg.type != 'registrant') {
            data.cira_cpr_category = 'CCT'
        }
        cg.edit = data
        validateContactGroup(cg)
        markUpdated()
    }

    function copyFrom(cg: IContactGroup, srcCg: IContactGroup) {
        const data = { ...srcCg.edit }
        if ($nonIndividualCprCategoryCodes.has(data.cira_cpr_category) && cg.type != 'registrant') {
            data.cira_cpr_category = 'CCT'
        }
        cg.edit = data
        validateContactGroup(cg)
        markUpdated()
    }

    function resetContactGroup(cg: IContactGroup) {
        cg.edit = { ...cg.source }
        validateContactGroup(cg)
        markUpdated()
    }

    function clearContactGroup(cg: IContactGroup) {
        cg.edit = makeEmptyContactData()
        validateContactGroup(cg)
        markUpdated()
    }

    function toggleOtherMenu(key: string) {
        loadFromOtherOpen[key] = !loadFromOtherOpen[key]
        for (const [k, v] of Object.entries(loadFromOtherOpen)) {
            if (v && k != key) loadFromOtherOpen[k] = false
        }
    }

    // ##############################################################  Edition
    // Import an edited contact into the actual group. A contact should already
    // be valid if we could endup here.
    function importEdition(cg: IContactGroup) {
        // $contactGroupEdit contains the newly edited data
        // contactGroupInEdition is the "original" contact group being edited.

        if (contactGroupInEdition == null) return

        contactGroups[contactGroupInEdition].edit = { ...cg.edit }
        validateContactGroup(contactGroups[contactGroupInEdition])
        markUpdated()

        contactGroupInEdition = null // this closes the modal
    }

    const asAny = (x: any) => x as any

    // loadEditedContacts skips it ready is false
    // onMount(() => {
    //     loadEditedContacts(contacts)
    // })
    $effect(() => {
        contacts
        tick().then(() => {
            loadEditedContacts(contacts)
        })
    })
    $effect(() => {
        if (setContacts != null) {
            const sc = [...setContacts]
            setContacts = null
            loadEditedContacts(sc)
        }
    })
    // $effect(() => {
    //     // if ($contactTypeOrder != null && contacts && $cprIndex && $cprCategories != null && $contactRulesSets != null && requiredContactTypes != null) {
    //     if ($cprCategories != null) {
    //         untrack(() => {
    //             loadContacts(contacts)
    //         })
    //         ready = true
    //     }
    // })
    unsubs.push(
        cprCategories.subscribe((v) => {
            if (v != null) {
                untrack(() => {
                    loadContacts(contacts)
                })
                ready = true
            }
        }),
    )

    $effect(() => {
        if (resetContacts == true) {
            tick().then(() => {
                resetContacts = false
                loadContacts(contacts)
            })
        }
    })
</script>

<style>
    li.dd-menu-row {
        white-space: nowrap;
    }
    li.dd-menu-row > span.disabled {
        cursor: not-allowed;
    }
    .btn-sm,
    .btn-sm,
    .btn-group-sm > .btn {
        --bs-btn-font-size: 0.65rem;
        --bs-btn-border-radius: 0.125rem;
    }
</style>

<svelte:body
    onclick={(e) => {
        for (const [k, v] of Object.entries(loadFromOtherOpen)) {
            if (v) loadFromOtherOpen[k] = false
        }
    }}
/>

{#if ready}
    <div class="contact-container">
        {#each contactGroups as cg, cgi}
            <div class="contact contact-status-{cg.status == 'ok' && cg.sourceEmpty && cg.editEmpty ? 'unset' : cg.status}">
                <div>
                    <div class="type">
                        <div class="type-name">
                            {$resolve(asAny(cg.name))}
                            {#if !cg.canEdit && !canEditRegistrant}
                                <span>*</span>
                            {/if}
                        </div>
                        <div class="actions">
                            {#if !updating}
                                <div class="btn-group btn-group-sm">
                                    {#if cg.canEdit}
                                        <div class="btn-group btn-group-sm">
                                            <div class="dropdown dropdown-selstyle">
                                                <button
                                                    id="contact-history"
                                                    class:btn-outline-light={!darkButtons(cg.status)}
                                                    class:btn-outline-dark={darkButtons(cg.status)}
                                                    class="btn dropdown-toggle btn-sm"
                                                    type="button"
                                                    onclick={(e) => {
                                                        e.stopPropagation()
                                                        toggleOtherMenu(cg.id)
                                                    }}
                                                    aria-haspopup="true"
                                                    aria-expanded="true"
                                                    disabled={updating}
                                                    data-tooltip={$_('Load from another contact')}
                                                >
                                                    <span class="sel-text"><Icon inline icon="fa:address-card-o" /></span>
                                                    <Icon inline icon="fa:level-down" />
                                                </button>
                                                <ul class="dropdown-menu drowdown-menu-right" class:show={loadFromOtherOpen[cg.id]} role="menu" aria-labelledby="contact-history">
                                                    {#each contactGroups as altcg}
                                                        <li role="menuitem" class="dd-menu-row">
                                                            <span
                                                                class:disabled={altcg == cg || altcg.editEmpty}
                                                                class:text-muted={altcg == cg || altcg.editEmpty}
                                                                role="button"
                                                                tabindex="0"
                                                                onkeypress={(e) => {
                                                                    if ([' ', 'Enter'].includes(e.key)) {
                                                                        if (altcg != cg && !altcg.editEmpty) {
                                                                            copyFrom(cg, altcg)
                                                                            toggleOtherMenu(cg.id)
                                                                        }
                                                                    }
                                                                }}
                                                                onclick={(e) => {
                                                                    e.stopPropagation()
                                                                    if (altcg != cg && !altcg.editEmpty) {
                                                                        copyFrom(cg, altcg)
                                                                        toggleOtherMenu(cg.id)
                                                                    }
                                                                }}>{$resolve(asAny(altcg.name))}</span
                                                            >
                                                        </li>
                                                    {/each}
                                                </ul>
                                            </div>
                                        </div>
                                    {/if}
                                    <button class:btn-outline-light={!darkButtons(cg.status)} class:btn-outline-dark={darkButtons(cg.status)} class="btn" type="button" disabled={cg.status == 'clean' || cg.status == 'unset'} class:d-none={cg.status == 'clean' || cg.status == 'unset'} data-tooltip={$_('Copier')} onclick={() => copyContactGroup(cg)}><Icon inline icon="fa:copy" /></button>
                                    {#if cg.canEdit}
                                        <button class:btn-outline-light={!darkButtons(cg.status)} class:btn-outline-dark={darkButtons(cg.status)} class="btn" type="button" disabled={cgClipboard == null} class:d-none={cgClipboard == null} data-tooltip={$_('Paste')} onclick={() => pasteContactGroup(cg)}><Icon inline icon="fa:paste" /></button>
                                    {/if}
                                    {#if cg.canEdit && !cg.sourceEmpty}
                                        <button class:btn-outline-light={!darkButtons(cg.status)} class:btn-outline-dark={darkButtons(cg.status)} class="btn" type="button" disabled={!cg.dirty} class:d-none={!cg.dirty} data-tooltip={$_('Revert to current')} onclick={() => resetContactGroup(cg)}><Icon inline icon="fa:undo" /></button>
                                    {/if}
                                    {#if cg.canEdit}
                                        <button class:btn-outline-light={!darkButtons(cg.status)} class:btn-outline-dark={darkButtons(cg.status)} class="btn" type="button" data-tooltip={$_('Edit contact')} onclick={() => (contactGroupInEdition = cgi)}><Icon inline icon="fa:pencil" /></button>
                                    {/if}
                                    {#if cg.canEdit && !cg.isRequired}
                                        <button class:btn-outline-light={!darkButtons(cg.status)} class:btn-outline-dark={darkButtons(cg.status)} class="btn" type="button" disabled={cg.status == 'clean' || cg.status == 'unset'} class:d-none={cg.status == 'clean' || cg.status == 'unset'} data-tooltip={$_('Remove')} onclick={() => clearContactGroup(cg)}><Icon inline icon="fa:times" /></button>
                                    {/if}
                                </div>
                            {/if}
                        </div>
                    </div>
                    {#if cg.sourceEmpty && cg.editEmpty}
                        <div class="infos-contact unconfigured">
                            <div>
                                {$_('This contact is not set')}

                                {#if cg.canEdit}
                                    <button class="btn btn-outline-secondary btn-sm" type="button" data-tooltip={$_('Edit contact')} onclick={() => (contactGroupInEdition = cgi)}>{$_('Configurer')}</button>
                                {/if}
                            </div>
                        </div>
                    {:else}
                        <div class="infos-contact">
                            {#if $siteContext == 'admin' && cg.original && cg.original.cira && cg.original.cira.id != null}
                                <div class="cira-id">{cg.original.cira.id}</div>
                            {/if}
                            {#if asAny(cg.fields).has('cira_cpr_category')}
                                <div class="field-cira_cpr_category field field-status-{asAny(cg.fieldStatus).cira_cpr_category}" class:field-invalid={asAny(cg.invalidFields).contextual.has('cira_cpr_category')}>
                                    <span class="source">{cprCategoryName(cg.source.cira_cpr_category)}</span>
                                    <span class="edit" title={cprCategoryName(cg.source.cira_cpr_category)}>{cprCategoryName(cg.edit.cira_cpr_category)}</span>
                                    <span class="empty">{$_('No Canadian presence')}</span>
                                </div>
                            {/if}
                            {#if asAny(cg.fields).has('cira_prefered_lang')}
                                <div class="f_cira_prefered_lang field field-status-{asAny(cg.fieldStatus).cira_prefered_lang}" class:field-invalid={asAny(cg.invalidFields).contextual.has('cira_prefered_lang')}>
                                    <span class="source">{cg.source.cira_prefered_lang == 'fr' ? $_('French') : $_('English')}</span>
                                    <span class="edit" title={cg.source.cira_prefered_lang == 'fr' ? $_('French') : $_('English')}>{cg.edit.cira_prefered_lang == 'fr' ? $_('French') : $_('English')}</span>
                                </div>
                            {/if}
                            {#if asAny(cg.fields).has('cira_cpr_category') || asAny(cg.fields).has('cira_prefered_lang')}
                                <br />
                            {/if}

                            {#if asAny(cg.fields).has('name')}
                                <div class="name field field-status-{asAny(cg.fieldStatus).name}" class:field-invalid={asAny(cg.invalidFields).contextual.has('name')}>
                                    <span class="source">{cg.source.name}</span>
                                    <span class="edit" title={cg.source.name ? cg.source.name : '–'}>{cg.edit.name}</span>
                                </div>
                            {/if}
                            {#if asAny(cg.fields).has('org')}
                                <div class="org field field-status-{asAny(cg.fieldStatus).org}" class:field-invalid={asAny(cg.invalidFields).contextual.has('org')}>
                                    <span class="source">{cg.source.org}</span>
                                    <span class="edit" title={cg.source.org ? cg.source.org : '–'}>{cg.edit.org}</span>
                                </div>
                            {/if}
                            {#if (asAny(cg.fieldStatus).name != 'empty' || asAny(cg.fieldStatus).org != 'empty') && (asAny(cg.fields).has('name') || asAny(cg.fields).has('org'))}
                                <br />
                            {/if}
                            {#if asAny(cg.fields).has('email')}
                                <div class="email field field-status-{asAny(cg.fieldStatus).email}" class:field-invalid={asAny(cg.invalidFields).contextual.has('email')}>
                                    <span class="source">{cg.source.email}</span>
                                    <span class="edit" title={cg.source.email ? cg.source.email : '–'}>{cg.edit.email}</span>
                                </div>
                            {/if}
                            {#if asAny(cg.fields).has('street')}
                                <div class="street field field-status-{asAny(cg.fieldStatus).street}" class:field-invalid={asAny(cg.invalidFields).contextual.has('street')}>
                                    <span class="source">
                                        {#if cg.source.street}{#each cg.source.street as s, i}
                                                {#if i > 0}<br />{/if}
                                                {s}
                                            {/each}{/if}
                                    </span>
                                    <span class="edit" title={cg.source.street && cg.source.street.length ? cg.source.street.join('; ') : '–'}>
                                        {#if cg.edit.street}{#each cg.edit.street as s, i}
                                                {#if i > 0}<br />{/if}
                                                {s}
                                            {/each}{/if}
                                    </span>
                                </div>
                            {/if}
                            <div class="field-group-address">
                                {#if asAny(cg.fields).has('city')}
                                    <span class="field-city contact-right-space field field-status-{asAny(cg.fieldStatus).city}" class:field-invalid={asAny(cg.invalidFields).contextual.has('city')}>
                                        <span class="source">{cg.source.city}</span>
                                        <div class="edit" title={cg.source.city ? cg.source.city : '–'}>{cg.edit.city}</div>
                                    </span>
                                {/if}
                                {#if asAny(cg.fields).has('sp')}
                                    <span class="field-sp contact-right-space field field-status-{asAny(cg.fieldStatus).sp}" class:field-invalid={asAny(cg.invalidFields).contextual.has('sp')}>
                                        <span class="source">{cg.source.sp}</span>
                                        <div class="edit" title={cg.source.sp ? cg.source.sp : '–'}>{cg.edit.sp}</div>
                                    </span>
                                {/if}
                                {#if asAny(cg.fields).has('postal_code')}
                                    <span class="field-postal_code contact-right-space field field-status-{asAny(cg.fieldStatus).postal_code}" class:field-invalid={asAny(cg.invalidFields).contextual.has('postal_code')}>
                                        <span class="source">{formatPostalCode(cg.source.country, cg.source.postal_code, '–')}</span>
                                        <div class="edit" title={formatPostalCode(cg.source.country, cg.source.postal_code, '–')}>{formatPostalCode(cg.edit.country, cg.edit.postal_code, '–')}</div>
                                    </span>
                                {/if}
                            </div>
                            {#if asAny(cg.fields).has('country')}
                                <div class="country field field-status-{asAny(cg.fieldStatus).country}" class:field-invalid={asAny(cg.invalidFields).contextual.has('country')}>
                                    <span class="source">{cg.source.country}</span>
                                    <span class="edit" title={cg.source.country ? cg.source.country : '–'}>{cg.edit.country}</span>
                                </div>
                            {/if}
                            {#if asAny(cg.fields).has('voice') || asAny(cg.fields).has('fax')}
                                <br />
                            {/if}
                            {#if asAny(cg.fields).has('voice')}
                                <div class="voice field field-status-{asAny(cg.fieldStatus).voice}" class:field-invalid={asAny(cg.invalidFields).contextual.has('voice')}>
                                    <span class="source">{formatPhoneNumber(cg.source.voice, '–')}</span>
                                    <span class="edit" title={formatPhoneNumber(cg.source.voice, '–')}>{formatPhoneNumber(cg.edit.voice, '–')}</span>
                                </div>
                            {/if}
                            {#if asAny(cg.fields).has('fax')}
                                <div class="fax field field-status-{asAny(cg.fieldStatus).fax}" class:field-invalid={asAny(cg.invalidFields).contextual.has('fax')}>
                                    <span class="source">{cg.source.fax ? formatPhoneNumber(cg.source.fax, '–') : ''}</span>
                                    <span class="edit" title={cg.source.fax ? formatPhoneNumber(cg.source.fax, '–') : ''}>{cg.edit.fax ? formatPhoneNumber(cg.edit.fax, '–') : ''}</span>
                                    <span class="text-muted">({$_('fax')})</span>
                                </div>
                            {/if}
                        </div>
                    {/if}
                    {#if cg.type == 'registrant' && !cg.canEdit && !canEditRegistrant}
                        <div class="infos-contact registrant-edit-msg">
                            * {@html $_('To modify this contact, please contact us by phone at <a href="tel:8778809224">877 880-9224</a> or by email at <a href="mailto:tech@webdomaine.ca">tech@webdomaine.ca</a>.')}
                        </div>
                    {/if}
                </div>
            </div>
        {/each}
    </div>
{/if}

{#if contactGroupEdit != null}
    <ContactEditor
        context="domain"
        contactGroup={contactGroupEdit}
        close={() => {
            contactGroupInEdition = null
        }}
        save={(cg) => {
            importEdition(cg)
        }}
        {inlineMode}
        {addresses}
        autoLg={lg != null ? lg : true}
        {updateGroupValidity}
    />
{/if}
